const field = (group, key, label, description, options = {}) => Object.freeze({
    key,
    label,
    group,
    required: options.required ?? false,
    multiple: options.multiple ?? false,
    description,
});
/**
 * The target fields: the common fields of a catalog item and the fields of each item type. Codes are
 * snake_case. Staff map a source column to one of them; a later phase validates the values.
 */
export const importTargetFields = Object.freeze([
    // common
    field("common", "name", "Name", "The name of the item.", { required: true }),
    field("common", "item_type", "Item type", "Dining, villa, event, experience or service. If no column has it, choose one type for the whole import."),
    field("common", "category", "Category", "The primary category."),
    field("common", "short_description", "Short description", "One or two sentences."),
    field("common", "description", "Description", "The full description."),
    field("common", "address_text", "Address", "The street address as text."),
    field("common", "location_city", "Location › City", "The city or town."),
    field("common", "region", "Region", "The state, province or region."),
    field("common", "country_code", "Country", "The country, as a name or a two-letter code."),
    field("common", "latitude", "Latitude", "Decimal degrees."),
    field("common", "longitude", "Longitude", "Decimal degrees."),
    field("common", "time_zone", "Time zone", "An IANA time zone, for example Asia/Kolkata."),
    field("common", "price_text", "Price", "The price as the source writes it. It is never trusted as a payable price."),
    field("common", "price_currency", "Currency", "The currency of the price."),
    field("common", "external_url", "Website", "A link to the item's own page."),
    field("common", "source_key", "Source ID", "The identifier of the row in the source system."),
    field("common", "google_place_id", "Google Place ID", "The Google Maps Place ID. Strong evidence of a duplicate."),
    // event
    field("event", "venue_name", "Venue", "The name of the venue."),
    field("event", "start_date", "Start date", "The whole date in one column."),
    field("event", "start_day", "Start day", "The day of the month, when the date is split in columns."),
    field("event", "start_month", "Start month", "The month, when the date is split in columns."),
    field("event", "start_year", "Start year", "The year, when the date is split in columns."),
    field("event", "start_time", "Start time", "The time of day the event starts."),
    field("event", "end_date", "End date", "The whole end date in one column."),
    field("event", "end_time", "End time", "The time of day the event ends."),
    field("event", "performers", "Performers", "A performer or a celebrity. Map several columns to keep several performers, in column order.", { multiple: true }),
    // dining
    field("dining", "cuisine", "Cuisine", "The kinds of cuisine.", { multiple: true }),
    field("dining", "dress_code", "Dress code", "The dress code."),
    field("dining", "reservation_policy", "Reservation policy", "Whether a reservation is needed."),
    // villa
    field("villa", "bedrooms", "Bedrooms", "The number of bedrooms."),
    field("villa", "bathrooms", "Bathrooms", "The number of bathrooms."),
    field("villa", "max_guests", "Maximum guests", "The most guests the villa takes."),
    field("villa", "minimum_stay_nights", "Minimum stay (nights)", "The shortest stay."),
    // experience
    field("experience", "duration_minutes", "Duration (minutes)", "How long the experience lasts."),
    field("experience", "group_min", "Smallest group", "The smallest group size."),
    field("experience", "group_max", "Largest group", "The largest group size."),
    field("experience", "minimum_age", "Minimum age", "The youngest age that may take part."),
    // service
    field("service", "service_area_text", "Service area", "Where the service is offered."),
    field("service", "lead_time_hours", "Lead time (hours)", "How far ahead the service must be booked."),
]);
const BY_KEY = new Map(importTargetFields.map((target) => [target.key, target]));
export function findImportTargetField(key) {
    return BY_KEY.get(key);
}
export function isImportTargetField(key) {
    return BY_KEY.has(key);
}
